import React, { useState } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { usePlace } from 'commons/usePlace'
import { FormattedMessage } from 'react-intl'
import { Typography } from '@material-ui/core'
import ConfirmationDialog from 'app/components/ConfirmationDialog'
import { ButtonWithLoading } from 'app/components/FormsyHelperComponent'
import CancelButton from 'app/components/buttons/CancelButton'
import * as R from 'ramda'
import { useDispatch } from 'react-redux'
import MessageAlert from 'app/components/MessageAlert'
import { useIntl } from 'react-intl'
import { updatePlaceByProp } from 'app/store/actions/place.actions'
import effects from 'app/effects/effects'
import { useAppName } from 'commons/usePartner'
import IosSwitchFormik from 'app/components/IosSwitchFormik'
import { Form, useFormik, FormikProvider } from 'formik'

export const ProfileStatus = { PUBLIC: 1, PRIVATE: 2 }

const useStyles = makeStyles((theme) => ({
  makePrivateDialogPaper: {
    backgroundColor: theme.palette.common.white,
    color: theme.palette.darkGrey.main,
    '& p': {
      color: theme.palette.darkGrey.main,
    },
  },
  darkGreyText: {
    color: theme.palette.darkGrey.main,
  },
}))

const togglePublicProfileStatus = R.cond([
  [R.equals(ProfileStatus.PUBLIC), R.always(ProfileStatus.PRIVATE)],
  [R.equals(ProfileStatus.PRIVATE), R.always(ProfileStatus.PUBLIC)],
  [R.T, R.always(ProfileStatus.PUBLIC)],
])

const TogglePlacePublic = () => {
  const place = usePlace()
  const classes = useStyles()
  const appName = useAppName()
  const dispatch = useDispatch()
  const { formatMessage } = useIntl()

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isOpenMakePrivateDialog, setIsOpenMakePrivateDialog] = useState(false)

  const formik = useFormik({
    initialValues: {
      placePublic: place.mobileVisibilityStatus === ProfileStatus.PUBLIC,
    },
  })

  const handkeUpdatePlaceStatus = (profileStatus) => {
    setIsSubmitting(true)
    effects.apis
      .savePlacePublic({
        placeId: place.id,
        data: { mobileVisibilityStatus: profileStatus },
      })
      .then(() => {
        dispatch(updatePlaceByProp('mobileVisibilityStatus', profileStatus))
        formik.setFieldValue(
          'placePublic',
          profileStatus === ProfileStatus.PUBLIC,
        )
        setIsSubmitting(false)
        setIsOpenMakePrivateDialog(false)
      })
      .catch((error) => {
        dispatch(MessageAlert.showNetworkError(formatMessage)(error))
        setIsSubmitting(false)
      })
  }

  const confirmPublicDialog = (
    isOpen,
    onSubmit,
    onCancel,
    isSubmitting,
    isPrivate,
  ) => {
    const dialog = isPrivate
      ? {
          title: 'profile.dialog.makePrivate.title',
          body: 'profile.dialog.makePrivate.description',
          confirmButtonLabel: 'profile.dialog.makePrivate.button.label',
        }
      : {
          title: 'profile.dialog.makePublic.title',
          body: 'profile.dialog.makePublic.description',
          confirmButtonLabel: 'profile.dialog.makePublic.button.label',
        }
    return (
      <ConfirmationDialog
        PaperProps={{ className: classes.makePrivateDialogPaper }}
        open={isOpen}
        title={<FormattedMessage id={dialog.title} />}
        body={
          <FormattedMessage
            id={dialog.body}
            values={{
              newLine: <br />,
              appName,
            }}
          />
        }
        CancelButton={<CancelButton onClick={onCancel} />}
        ConfirmButton={
          <ButtonWithLoading onClick={onSubmit} isLoading={isSubmitting}>
            {<FormattedMessage id={dialog.confirmButtonLabel} />}
          </ButtonWithLoading>
        }
      />
    )
  }

  const toggleLable = (bool) => {
    const text = bool
      ? 'profile.toggle.place.public'
      : 'profile.toggle.place.private'
    return (
      <Typography className="font-normal text-14 flex-none">
        {formatMessage({
          id: text,
        })}
      </Typography>
    )
  }

  return (
    <>
      <FormikProvider value={formik}>
        <Form>
          <div className="flex space-x-3 items-center w-max">
            <IosSwitchFormik
              name="placePublic"
              onChange={() => {
                formik.setFieldValue('placePublic', formik.values.placePublic)
                setIsOpenMakePrivateDialog(true)
              }}
              inputProps={{
                'data-testid': `placePublic`,
              }}
            />
            {toggleLable(formik.values.placePublic)}
          </div>
        </Form>
      </FormikProvider>

      {confirmPublicDialog(
        isOpenMakePrivateDialog,
        () =>
          R.o(
            handkeUpdatePlaceStatus,
            togglePublicProfileStatus,
            place.mobileVisibilityStatus,
          ),
        () => setIsOpenMakePrivateDialog(false),
        isSubmitting,
        formik.values.placePublic,
      )}
    </>
  )
}

export default TogglePlacePublic
